'use client';

import { useEffect, useRef, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

if (typeof window !== 'undefined') gsap.registerPlugin(ScrollTrigger);

/* One gentle feel for the whole landing: a slow fade with only a small drift
   up, a soft sine ease (no snap at the start or end), and a relaxed stagger
   between things that arrive together. */
const REVEAL = { y: 10, duration: 1.1, stagger: 0.12, ease: 'sine.out' } as const;

/**
 * Fade + rise every `[data-reveal]` element inside the returned ref, once, as
 * it scrolls into view. Elements are batched by when they ENTER, not by
 * section: a desktop row of cards arrives together (staggered in DOM order),
 * while the same cards stacked on a phone each arrive as they reach the screen.
 *
 * - Anything already on screen when the page binds is left exactly as rendered
 *   (no hide-then-show blink above the fold).
 * - The inline opacity/transform is cleared once an element lands, so the
 *   element's own hover / CSS transforms take over again.
 * - Reduced motion: nothing is hidden or animated.
 *
 * Pass `external` to reuse a ref the section already holds.
 */
export function useReveal<T extends HTMLElement>(external?: RefObject<T | null>) {
  const own = useRef<T>(null);
  const ref = external ?? own;
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const vh = window.innerHeight;
    const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]')).filter(
      (el) => el.getBoundingClientRect().top > vh * 0.92,
    );
    if (!targets.length) return undefined;

    /* fade to each element's OWN resting opacity (some sit at 0.6–0.7 by
       design), so clearing the inline style at the end doesn't jump */
    const rest = new Map(targets.map((el) => [el, Number(getComputedStyle(el).opacity)]));
    gsap.set(targets, { opacity: 0, y: REVEAL.y });
    const triggers = ScrollTrigger.batch(targets, {
      // a touch earlier than the bottom edge, so the slower fade is well under
      // way by the time the reader's eye gets there
      start: 'top 92%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          opacity: (_i: number, el: HTMLElement) => rest.get(el) ?? 1,
          y: 0,
          duration: REVEAL.duration,
          ease: REVEAL.ease,
          stagger: REVEAL.stagger,
          overwrite: true,
          clearProps: 'opacity,transform',
        }),
    });

    return () => {
      triggers.forEach((st) => st.kill());
      gsap.killTweensOf(targets);
      gsap.set(targets, { clearProps: 'opacity,transform' });
    };
  }, [ref]);
  return ref;
}
